import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, LogOut, Upload, Save, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ code: "", percent_off: 10 });

  useEffect(() => {
    if (!localStorage.getItem("um_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => { setReady(true); loadAll(); })
      .catch(() => { localStorage.removeItem("um_token"); navigate("/admin/login"); });
  }, []);

  const loadAll = () => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/messages").then((r) => setMessages(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/admin/discounts").then((r) => setDiscounts(r.data));
  };

  const logout = () => { localStorage.removeItem("um_token"); navigate("/admin/login"); };

  const updateProductField = (id, field, value) =>
    setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const saveProduct = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        title: p.title, tagline: p.tagline, description: p.description,
        price: parseFloat(p.price), compare_at_price: parseFloat(p.compare_at_price),
      });
      toast.success(`${p.title} saved`);
    } catch { toast.error("Save failed"); }
  };

  const uploadPdf = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/products/${id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", {
        brand_name: settings.brand_name, promo_active: settings.promo_active,
        promo_text: settings.promo_text, promo_end: settings.promo_end,
        social_instagram: settings.social_instagram, social_email: settings.social_email,
      });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  const createDiscount = async () => {
    if (!newDiscount.code) return;
    try {
      await api.post("/admin/discounts", { code: newDiscount.code, percent_off: parseInt(newDiscount.percent_off), active: true });
      setNewDiscount({ code: "", percent_off: 10 });
      api.get("/admin/discounts").then((r) => setDiscounts(r.data));
      toast.success("Discount saved");
    } catch { toast.error("Failed"); }
  };

  if (!ready) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      <div className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <p className="font-display text-xl font-extrabold tracking-tight">UpgradeMate <span className="text-muted-foreground">Admin</span></p>
          <Button variant="outline" onClick={logout} className="rounded-none border-black/20" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logout</Button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <Tabs defaultValue="products">
          <TabsList className="rounded-none bg-white">
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">Discounts</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6 space-y-4">
            {products.map((p) => (
              <div key={p.id} className="border border-black/10 bg-white p-6" data-testid={`admin-product-${p.slug}`}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <label className="text-xs uppercase tracking-wide text-muted-foreground">Title {p.is_bundle && "(Bundle)"}</label>
                    <Input value={p.title} onChange={(e) => updateProductField(p.id, "title", e.target.value)} className="rounded-none border-black/20" />
                    <label className="text-xs uppercase tracking-wide text-muted-foreground">Tagline</label>
                    <Input value={p.tagline} onChange={(e) => updateProductField(p.id, "tagline", e.target.value)} className="rounded-none border-black/20" />
                    <div className="flex gap-3">
                      <div className="flex-1"><label className="text-xs uppercase tracking-wide text-muted-foreground">Price</label>
                        <Input type="number" value={p.price} onChange={(e) => updateProductField(p.id, "price", e.target.value)} className="rounded-none border-black/20" /></div>
                      <div className="flex-1"><label className="text-xs uppercase tracking-wide text-muted-foreground">Compare</label>
                        <Input type="number" value={p.compare_at_price} onChange={(e) => updateProductField(p.id, "compare_at_price", e.target.value)} className="rounded-none border-black/20" /></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-xs uppercase tracking-wide text-muted-foreground">Description</label>
                    <Textarea rows={5} value={p.description} onChange={(e) => updateProductField(p.id, "description", e.target.value)} className="rounded-none border-black/20" />
                    <div className="flex items-center gap-3">
                      <label className="inline-flex cursor-pointer items-center gap-2 border border-black/20 px-4 py-2 text-sm">
                        <Upload className="h-4 w-4" /> Upload PDF
                        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(p.id, e.target.files[0])} data-testid={`upload-pdf-${p.slug}`} />
                      </label>
                      {p.pdf_filename && <span className="text-xs text-muted-foreground">{p.pdf_filename}</span>}
                    </div>
                  </div>
                </div>
                <Button onClick={() => saveProduct(p)} className="mt-4 rounded-none bg-primary text-primary-foreground" data-testid={`save-product-${p.slug}`}><Save className="h-4 w-4" /> Save</Button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="overflow-x-auto border border-black/10 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-black/10 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="p-4">Email</th><th className="p-4">Item</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Date</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.session_id} className="border-b border-black/5">
                      <td className="p-4">{o.email}</td>
                      <td className="p-4 capitalize">{o.slug}</td>
                      <td className="p-4">${(o.amount / 100).toFixed(2)}</td>
                      <td className="p-4"><span className={o.payment_status === "paid" ? "text-emerald-600" : "text-muted-foreground"}>{o.payment_status}</span></td>
                      <td className="p-4 text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="border border-black/10 bg-white p-6">
              {leads.length === 0 ? <p className="text-muted-foreground">No leads yet.</p> :
                leads.map((l, i) => <div key={i} className="flex justify-between border-b border-black/5 py-2 text-sm"><span>{l.email}</span><span className="text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</span></div>)}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6 space-y-3">
            {messages.length === 0 ? <p className="text-muted-foreground">No messages yet.</p> :
              messages.map((m) => <div key={m.id} className="border border-black/10 bg-white p-5"><div className="flex justify-between"><p className="font-medium">{m.name} · {m.email}</p><span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span></div><p className="mt-2 text-sm text-muted-foreground">{m.message}</p></div>)}
          </TabsContent>

          <TabsContent value="discounts" className="mt-6">
            <div className="border border-black/10 bg-white p-6">
              <div className="flex flex-wrap items-end gap-3">
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Code</label><Input value={newDiscount.code} onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value })} className="rounded-none border-black/20" placeholder="SUMMER20" data-testid="discount-code-input" /></div>
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">% Off</label><Input type="number" value={newDiscount.percent_off} onChange={(e) => setNewDiscount({ ...newDiscount, percent_off: e.target.value })} className="rounded-none border-black/20 w-24" /></div>
                <Button onClick={createDiscount} className="rounded-none bg-primary text-primary-foreground" data-testid="create-discount-btn"><Plus className="h-4 w-4" /> Add</Button>
              </div>
              <div className="mt-6 space-y-2">
                {discounts.map((d) => <div key={d.code} className="flex justify-between border-b border-black/5 py-2 text-sm"><span className="font-display">{d.code}</span><span>{d.percent_off}% · {d.active ? "active" : "inactive"}</span></div>)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            {settings && (
              <div className="max-w-lg space-y-4 border border-black/10 bg-white p-6">
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Brand Name</label><Input value={settings.brand_name || ""} onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })} className="rounded-none border-black/20" data-testid="settings-brand" /></div>
                <div className="flex items-center justify-between"><label className="text-sm">Promo bar active</label><Switch checked={!!settings.promo_active} onCheckedChange={(v) => setSettings({ ...settings, promo_active: v })} data-testid="settings-promo-toggle" /></div>
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Promo Text</label><Input value={settings.promo_text || ""} onChange={(e) => setSettings({ ...settings, promo_text: e.target.value })} className="rounded-none border-black/20" /></div>
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Promo End (ISO)</label><Input value={settings.promo_end || ""} onChange={(e) => setSettings({ ...settings, promo_end: e.target.value })} className="rounded-none border-black/20" /></div>
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Instagram URL</label><Input value={settings.social_instagram || ""} onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })} className="rounded-none border-black/20" /></div>
                <div><label className="text-xs uppercase tracking-wide text-muted-foreground">Contact Email</label><Input value={settings.social_email || ""} onChange={(e) => setSettings({ ...settings, social_email: e.target.value })} className="rounded-none border-black/20" /></div>
                <Button onClick={saveSettings} className="rounded-none bg-primary text-primary-foreground" data-testid="save-settings"><Save className="h-4 w-4" /> Save Settings</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
